import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPersonSwimming, faChevronDown, faChevronRight, faSpinner, faStopwatch, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { compareEvents, sortStrokeValues, parseEventName, collectStrokes, collectDistances, getMedleyLegsByDistance } from "../../services/utils/EventOrdering";
import { useCourseFilter, useGroupBy, groupLabel } from "../hooks/useEventFilters";
import { useCollapsible } from "../hooks/useCollapsible";
import { useSwimmerAPI } from "../context/SwimmerAPIContext";
import { EventFilterBar } from "./filters";
import SwimTimeTrendChart from "./SwimTimeTrendChart";
import SplitsTrendChart from "./SplitsTrendChart";
const PersonalBests = ({ times, tiref, selectedStrokes = [], onSelectedStrokesChange, selectedDistances = [], onSelectedDistancesChange }) => {
    const api = useSwimmerAPI();
    const [courseFilter, setCourseFilter] = useCourseFilter();
    const [collapsedCourses, toggleCourse] = useCollapsible();
    const [expandedRows, toggleRow, resetExpandedRows] = useCollapsible();
    const [groupBy, setGroupBy] = useGroupBy();
    const [history, setHistory] = useState({});
    const [splits, setSplits] = useState({});
    const [expandedSplits, toggleSplit, resetExpandedSplits] = useCollapsible();
    // Reset per-row expansion and fetched history when switching swimmers, so the
    // previous swimmers' attempts don't leak into an identically names event row.
    useEffect(() => {
        resetExpandedRows();
        setHistory({});
        resetExpandedSplits();
        setSplits({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiref]);
    const fetchHistory = (rowKey, sourceUrl) => {
        setHistory(prev => ({ ...prev, [rowKey]: 'loading' }));
        api.getTimeHistory(sourceUrl)
            .then(attempts => setHistory(prev => ({ ...prev, [rowKey]: attempts })))
            .catch(err => setHistory(prev => ({ ...prev, [rowKey]: err instanceof Error ? err : new Error('Failed to load previous attempts') })));
    };
    const handleToggleRow = (rowKey, sourceUrl) => {
        const willExpand = !expandedRows.has(rowKey);
        toggleRow(rowKey);
        if (willExpand && sourceUrl && !history[rowKey]) {
            fetchHistory(rowKey, sourceUrl);
        }
    };
    const fetchSplits = (splitsUrl) => {
        setSplits(prev => ({ ...prev, [splitsUrl]: 'loading' }));
        api.getSplits(splitsUrl)
            .then(rows => setSplits(prev => ({ ...prev, [splitsUrl]: rows })))
            .catch(err => setSplits(prev => ({
            ...prev,
            [splitsUrl]: err instanceof Error ? err : new Error('Failed to load splits')
        })));
    };
    const handleToggleSplit = (splitsUrl) => {
        const willExpand = !expandedSplits.has(splitsUrl);
        toggleSplit(splitsUrl);
        if (willExpand && !splits[splitsUrl]) {
            fetchSplits(splitsUrl);
        }
    };
    if (times.length === 0) {
        return React.createElement("div", { className: "empty-state" },
            React.createElement("p", null, "No times found"));
    }
    const strokes = collectStrokes(times.map(t => t.event));
    const distances = collectDistances(times.map(t => t.event));
    //Apply filters
    const filteredTimes = times.filter(time => {
        const { stroke, distance } = parseEventName(time.event);
        const courseMatch = courseFilter === 'all' || time.course === courseFilter;
        const strokeMatch = selectedStrokes.length === 0 || selectedStrokes.includes(stroke);
        const distanceMatch = selectedDistances.length === 0 || selectedDistances.includes(distance.toString());
        return courseMatch && strokeMatch && distanceMatch;
    });
    const sortedTimes = [...filteredTimes].sort((a, b) => compareEvents(a.event, b.event));
    //Group times by course
    const groupedByCourse = sortedTimes.reduce((acc, time) => {
        const course = time.course;
        if (!acc[course]) {
            acc[course] = [];
        }
        acc[course].push(time);
        return acc;
    }, {});
    const sortedCourses = Object.keys(groupedByCourse).sort((a, b) => {
        if (a === '50m')
            return -1;
        if (b === '50m')
            return 1;
        return a.localeCompare(b);
    });
    const groupedByStroke = sortedTimes.reduce((acc, time) => {
        const { stroke } = parseEventName(time.event);
        if (!acc[stroke])
            acc[stroke] = [];
        acc[stroke].push(time);
        return acc;
    }, {});
    const sortedStrokeGroups = sortStrokeValues(Object.keys(groupedByStroke));
    const activeGroups = groupBy === 'course' ? groupedByCourse : groupedByStroke;
    const activeGroupKeys = groupBy === 'course' ? sortedCourses : sortedStrokeGroups;
    const renderSplitsPanel = (splitsUrl, event) => {
        const entry = splits[splitsUrl];
        if (entry === 'loading') {
            return (React.createElement("div", { style: { padding: '0.5rem', color: 'var(--gray-400)', fontSize: '0.8rem' } },
                React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true }),
                " Loading splits..."));
        }
        if (entry instanceof Error) {
            return (React.createElement("div", { style: { padding: '0.5rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' } },
                React.createElement("span", { style: { color: 'var(--danger)' } },
                    "Failed to load splits: ",
                    entry.message),
                React.createElement("button", { type: "button", className: "btn-ghost", onClick: () => fetchSplits(splitsUrl) }, "Retry")));
        }
        if (!entry || entry.length === 0) {
            return (React.createElement("div", { style: { padding: '0.5rem', color: 'var(--gray-400)', fontSize: '0.8rem' } }, "No splits available"));
        }
        // Medleys are swum fly → back → breast → free, a quarter of the distance each,
        // so each split can be named. Empty for every other event.
        const legs = getMedleyLegsByDistance(event, entry.map(split => Number(split.distance)));
        return (React.createElement(React.Fragment, null,
            React.createElement(SplitsTrendChart, { splits: entry, event: event }),
            React.createElement("div", { className: "table-container splits-panel", style: { margin: 0 } },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Distance"),
                            legs.size > 0 && React.createElement("th", null, "Stroke"),
                            React.createElement("th", null, "Cumulative"),
                            React.createElement("th", null, "Split"))),
                    React.createElement("tbody", null, entry.map((split, i) => (React.createElement("tr", { key: i },
                        React.createElement("td", { "data-label": "Distance" }, split.distance),
                        legs.size > 0 && React.createElement("td", { "data-label": "Stroke" }, legs.get(Number(split.distance)) || '-'),
                        React.createElement("td", { "data-label": "Cumulative" }, split.cumulative),
                        React.createElement("td", { "data-label": "Split" }, split.incremental || '-')))))))));
    };
    const renderHistoryPanel = (rowKey, sourceUrl, event) => {
        const entry = history[rowKey];
        if (entry === 'loading') {
            return (React.createElement("div", { style: { padding: '0.5rem', color: 'var(--gray-400)', fontSize: '0.85rem' } },
                React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true }),
                " Loading previous attempts\u2026"));
        }
        if (entry instanceof Error) {
            return (React.createElement("div", { style: { padding: '0.5rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' } },
                React.createElement("span", { style: { color: 'var(--danger)' } },
                    "Failed to load previous attempts: ",
                    entry.message),
                React.createElement("button", { type: "button", className: "btn-ghost", onClick: () => fetchHistory(rowKey, sourceUrl) }, "Retry")));
        }
        if (!entry || entry.length === 0) {
            return (React.createElement("div", { style: { padding: '0.5rem', color: 'var(--gray-400)', fontSize: '0.85rem' } }, "No previous attempts found"));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(SwimTimeTrendChart, { attempts: entry }),
            React.createElement("div", { className: "table-container", style: { margin: 0 } },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Time"),
                            React.createElement("th", { className: "hide-on-mobile" }, "Pts"),
                            React.createElement("th", { className: "hide-on-mobile" }, "Date"),
                            React.createElement("th", { className: "hide-on-mobile" }, "Meet"),
                            React.createElement("th", { className: "hide-on-mobile" }, "Venue"))),
                    React.createElement("tbody", null, entry.map((attempt, i) => (React.createElement(React.Fragment, { key: i },
                        React.createElement("tr", null,
                            React.createElement("td", { className: "card-event-td" },
                                React.createElement("div", { className: "card-event-header" },
                                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.4rem' } },
                                        attempt.splitsUrl && (React.createElement("button", { type: "button", className: "btn-ghost", onClick: () => handleToggleSplit(attempt.splitsUrl), title: expandedSplits.has(attempt.splitsUrl)
                                                ? 'Hide splits'
                                                : 'Show splits', style: { padding: '0.15rem 0.3rem', flexShrink: 0 } },
                                            React.createElement(FontAwesomeIcon, { icon: faStopwatch, style: { fontSize: '0.7rem', color: expandedSplits.has(attempt.splitsUrl) ? 'var(--primary)' : 'var(--gray-800)', } }))),
                                        React.createElement("span", null, attempt.time),
                                        React.createElement("span", { className: "mobile-supplement card-meta-row" }, ` •  ${attempt.waPoints}pts`)),
                                    React.createElement("span", { className: "mobile-supplement card-inline-time" }, attempt.date)),
                                (attempt.meet || attempt.venue) && (React.createElement("div", { className: "mobile-supplement card-meta-row" }, [attempt.meet, attempt.venue].filter(Boolean).join(' • ')))),
                            React.createElement("td", { "data-label": "Pts", className: "hide-on-mobile" }, attempt.waPoints),
                            React.createElement("td", { "data-label": "Date", className: "hide-on-mobile" }, attempt.date),
                            React.createElement("td", { "data-label": "Meet", className: "hide-on-mobile" }, attempt.meet),
                            React.createElement("td", { "data-label": "Venue", className: "hide-on-mobile" }, attempt.venue)),
                        attempt.splitsUrl && expandedSplits.has(attempt.splitsUrl) && (React.createElement("tr", { className: "splits-drilldown-row" },
                            React.createElement("td", { colSpan: 4, className: "card-event-td", style: { paddingTop: '0.1rem', paddingBottom: '0.6rem' } }, renderSplitsPanel(attempt.splitsUrl, event))))))))))));
    };
    return (React.createElement("div", { className: "tab-content active" },
        React.createElement(EventFilterBar, { strokes: strokes, selectedStrokes: selectedStrokes, onStrokesChange: onSelectedStrokesChange, distances: distances, selectedDistances: selectedDistances, onDistancesChange: onSelectedDistancesChange, groupBy: groupBy, onGroupByChange: setGroupBy }),
        filteredTimes.length === 0 ? (React.createElement("div", { className: "empty-state", style: { textAlign: 'center', padding: '2rem' } },
            React.createElement("p", null, "No times match the current filter"))) : (activeGroupKeys.map((groupKey, courseIndex) => (React.createElement("div", { key: groupKey, className: `club-group${courseIndex > 0 ? ' club-group-spaced' : ''}` },
            React.createElement("div", { className: "club-header", onClick: () => toggleCourse(groupKey) },
                React.createElement("h3", { className: "club-header-title" },
                    React.createElement(FontAwesomeIcon, { icon: faPersonSwimming }),
                    " ",
                    groupLabel(groupBy, groupKey)),
                React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsedCourses.has(groupKey) ? ' expanded' : ''}` })),
            !collapsedCourses.has(groupKey) && React.createElement("div", { className: "table-container" },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", { style: { width: '230px' } }, "Event"),
                            React.createElement("th", null, "Time"),
                            React.createElement("th", null, groupBy === 'course' ? (groupKey === '50m' ? 'Conv. to SC' : 'Conv. to LC') : 'Conv.'),
                            React.createElement("th", null, "Date"),
                            React.createElement("th", null, "Venue"))),
                    React.createElement("tbody", null, activeGroups[groupKey].map((time) => {
                        const rowKey = `${time.event}_${time.course}`;
                        return (React.createElement(React.Fragment, { key: rowKey },
                            React.createElement("tr", null,
                                React.createElement("td", { "data-label": "Event", className: "card-event-td" },
                                    React.createElement("div", { className: "card-event-header" },
                                        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.4rem' } },
                                            time.sourceUrl && (React.createElement("button", { type: "button", className: "btn-ghost", onClick: () => handleToggleRow(rowKey, time.sourceUrl), title: expandedRows.has(rowKey)
                                                    ? 'Hide previous attempts'
                                                    : 'Show previous attempts', style: { padding: '0.15rem 0.3rem', flexShrink: 0 } },
                                                React.createElement(FontAwesomeIcon, { icon: expandedRows.has(rowKey) ? faChevronDown : faChevronRight, style: { fontSize: '0.7rem', color: 'var(--gray-800)' } }))),
                                            React.createElement("span", null,
                                                groupBy !== 'course' && (React.createElement(React.Fragment, null,
                                                    React.createElement("span", { className: "course-pill" }, time.course === '50m' ? 'LC' : 'SC'),
                                                    "\u00A0\u00A0")),
                                                time.event,
                                                React.createElement("span", null, time.sourceUrl && (React.createElement(React.Fragment, null,
                                                    "\u00A0\u00A0",
                                                    React.createElement("a", { className: "stroke-link", href: time.sourceUrl, target: "_blank", rel: "noreferrer" },
                                                        React.createElement(FontAwesomeIcon, { icon: faArrowUpRightFromSquare }))))),
                                                time.waPoints ? ` • ${time.waPoints}pts` : '')),
                                        React.createElement("span", { className: "mobile-supplement card-inline-time" }, time.time)),
                                    React.createElement("div", { className: "mobile-supplement card-meta-row" },
                                        time.course === '50m' ? `SC: ${time.convertedToSC || '—'}` : `LC: ${time.convertedToLC || '—'}`,
                                        ' · ',
                                        time.date,
                                        time.venue ? ` · ${time.venue}` : '')),
                                React.createElement("td", { "data-label": "Time", className: "hide-on-mobile" }, time.time),
                                React.createElement("td", { "data-label": time.course === '50m' ? 'Conv. SC' : 'Conv. LC', className: "hide-on-mobile" }, time.course === '50m' ? time.convertedToSC || '-' : time.convertedToLC || '-'),
                                React.createElement("td", { "data-label": "Date", className: "hide-on-mobile" }, time.date),
                                React.createElement("td", { "data-label": "Venue", className: "hide-on-mobile" }, time.venue)),
                            expandedRows.has(rowKey) && time.sourceUrl && (React.createElement("tr", null,
                                React.createElement("td", { colSpan: 5, className: "card-event-td", style: { paddingTop: '0.1rem', paddingBottom: '0.6rem' } }, renderHistoryPanel(rowKey, time.sourceUrl, time.event))))));
                    }))))))))));
};
export default PersonalBests;
